import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Drawer, message, Select} from 'antd';
import type {
    AiTradeSession,
    AnalysisKey,
    DraftKey,
    NeutralTradeSession,
    OrderSubmissionSnapshot,
    PatternAiAction,
    SignalTradeDraft,
    SubmissionStatus,
} from '../types';
import {SignalDirectionSplitButton} from './SignalDirectionSplitButton';
import {useInstrumentSpec} from '../hooks/useInstrumentSpec';
import {normalizePrice} from '../utils/priceNormalization';
import {generateIdempotencyKey, intentToKeyChar} from '../utils/idempotency';
import {tradingService} from '../../../../services/tradingService';
import api from '../../../../services/api';
import {normalizeTradeDraftPrices, validateTradePlanPrices,} from '../utils/tradeDrafts';
import {useSignalExpiry} from '../hooks/useSignalExpiry';
import {checkAllExpiry} from '../utils/signalExpiry';
import {classifySubmissionError, formatSuccessfulOrderStatus, stableBusinessMessage,} from '../utils/submissionErrors';
import {showTradingConfirmDialog} from '../../../../components/TradingConfirmDialog';

interface AiQuickTradingPanelProps {
    open: boolean;
    onClose: () => void;
    tradeSession: AiTradeSession | NeutralTradeSession;
    submissionSnapshot: OrderSubmissionSnapshot | null;
    submissionStatus: SubmissionStatus;
    submissionMessage: string | null;
    dispatch: React.Dispatch<PatternAiAction>;
    apiKeyId?: number | null;
    isLiveTrading: boolean;
    instId: string;
    interval: string;
    analysisKey: AnalysisKey;
    aiAnalysisId: number;
    signalType: 'PATTERN' | 'ZONE' | 'ALGORITHM';
    signalId: number;
    signalDirection: 1 | -1 | 0;
    signalConfidence?: number;
    entryPrice: number;
    targetPrice: number;
    stopLossPrice: number;
    expectedHoldingBars?: number;
    expectedHoldingUntil?: number;
    holdingPeriodText?: string;
    analysisGeneratedAt: number;
    signalFormedAt: number;
    currentPrice: number;
    zoneType?: string;
    zoneUpper?: number;
    zoneLower?: number;
    latestClose?: number;
    expiryPolicy: { priceDeviationThreshold: number; maxCandleAge: number };
}

export const AiQuickTradingPanel: React.FC<AiQuickTradingPanelProps> = (props) => {
    const {
        open, onClose,
        apiKeyId, instId, interval,
        analysisKey, aiAnalysisId, signalType, signalId,
        signalDirection, signalConfidence,
        entryPrice, targetPrice, stopLossPrice,
    } = props;

    const {spec, loading: specLoading, error: specError} = useInstrumentSpec(apiKeyId, instId, open);
    const expiryParams = {
        currentPrice: props.currentPrice,
        entryPrice,
        expectedHoldingUntil: props.expectedHoldingUntil,
        expectedHoldingBars: props.expectedHoldingBars,
        signalFormedAt: props.signalFormedAt,
        interval,
        zoneType: props.zoneType,
        zoneUpper: props.zoneUpper,
        zoneLower: props.zoneLower,
        latestClose: props.latestClose,
        policy: props.expiryPolicy,
    };
    const expiry = useSignalExpiry(open ? expiryParams : null, open);

    const [result, setResult] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const frozenKeyRef = useRef<string | null>(null);
    const submitInFlightRef = useRef(false);
    const previewRequestSeqRef = useRef(0);
    const [preview, setPreview] = useState<{
        margin: number;
        size?: number;
        estimatedExecutionPrice?: number
    } | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [statusQueryLoading, setStatusQueryLoading] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
    const submitStatus = props.submissionStatus;
    const pendingStorageKey = 'pattern_ai_pending_order';

    const clearPendingSubmission = useCallback(() => {
        try {
            sessionStorage.removeItem(pendingStorageKey);
        } catch { /* 浏览器禁用存储时仍由内存中的幂等键保护 */
        }
    }, []);

    useEffect(() => {
        const query = window.matchMedia('(max-width: 767px)');
        const update = () => setIsMobile(query.matches);
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, []);

    const isNeutral = 0 === signalDirection;
    const confidenceAvailable = null != signalConfidence && Number.isFinite(signalConfidence)
        && 0 <= signalConfidence && 100 >= signalConfidence;
    const confidence = confidenceAvailable ? signalConfidence! : 50;
    const isLocked = 'submitting' === submitStatus || 'processing' === submitStatus
        || 'unknown' === submitStatus || 'under_review' === submitStatus
        || 'succeeded' === submitStatus;
    const isFormLocked = isLocked || expiry.expired;

    useEffect(() => {
        if (!open) {
            setResult(null);
            setError(null);
            setPreview(null);
            setPreviewError(null);
            return;
        }
        setIsDirty(false);
    }, [open, analysisKey]);

    useEffect(() => {
        frozenKeyRef.current = props.submissionSnapshot?.idempotencyKey ?? null;
    }, [props.submissionSnapshot?.idempotencyKey]);

    const selectedKey = props.tradeSession.selectedDraftKey;
    const draft = getSessionDraft(props.tradeSession, selectedKey);
    const priceValidationError = draft
        ? 'market' === draft.orderType ? validateMarketProtectivePrices(draft) : validateTradePlanPrices(draft)
        : null;
    const normalizedDraft = draft && spec ? normalizeTradeDraftPrices(draft, spec.tickSz) : null;
    const normalizedPriceValidationError = normalizedDraft
        ? 'market' === normalizedDraft.orderType
            ? validateMarketProtectivePrices(normalizedDraft) : validateTradePlanPrices(normalizedDraft)
        : null;
    const submissionPriceValidationError = priceValidationError
        ?? (normalizedPriceValidationError ? `按最小变动单位规范化后：${normalizedPriceValidationError}` : null);

    const updateDraft = useCallback((updates: Partial<SignalTradeDraft>) => {
        if (!selectedKey || isFormLocked) return;
        setIsDirty(true);
        props.dispatch({type: 'UPDATE_DRAFT', draftKey: selectedKey, updates});
    }, [selectedKey, isFormLocked, props.dispatch]);

    const handleSelectDirection = useCallback((key: string) => {
        setIsDirty(true);
        if (isNeutral) {
            props.dispatch({type: 'SELECT_TRADE_INTENT', draftKey: 'FORWARD' === key ? 'MANUAL_LONG' : 'MANUAL_SHORT'});
        } else {
            props.dispatch({type: 'SELECT_TRADE_INTENT', draftKey: key as DraftKey});
        }
    }, [isNeutral, props.dispatch]);

    useEffect(() => {
        const requestSeq = ++previewRequestSeqRef.current;
        if (!open || !draft || !apiKeyId || !spec || isFormLocked || submissionPriceValidationError) {
            setPreview(null);
            setPreviewLoading(false);
            return;
        }
        let cancelled = false;
        setPreviewLoading(true);
        setPreviewError(null);
        const timer = window.setTimeout(() => {
            tradingService.previewOrderSizing({
                apiKeyId,
                instId,
                allocationPercent: draft.allocationPercent,
                side: 'long' === draft.posSide ? 'buy' : 'sell',
                posSide: draft.posSide,
                orderType: draft.orderType,
                lever: draft.leverage,
                px: 'limit' === draft.orderType ? normalizePrice(draft.entryPrice, spec.tickSz) : undefined,
                takeProfitPrice: normalizePrice(draft.targetPrice, spec.tickSz),
                stopLossPrice: normalizePrice(draft.stopLossPrice, spec.tickSz),
                source: 'ai',
                aiAnalysisId,
                signalType,
                signalId,
                signalDirection,
                tradeIntent: draft.tradeIntent,
                forwardConfidence: confidenceAvailable ? confidence : undefined,
            }).then((response: any) => {
                if (cancelled || requestSeq !== previewRequestSeqRef.current) return;
                const data = response?.data?.data;
                const margin = Number(data?.actualMargin ?? data?.requestedMargin);
                const size = Number(data?.size ?? data?.orderSize);
                const estimatedExecutionPrice = Number(data?.estimatedExecutionPrice);
                if (response?.data?.success && Number.isFinite(margin) && 0 < margin) {
                    setPreview({
                        margin,
                        size: Number.isFinite(size) ? size : undefined,
                        estimatedExecutionPrice: Number.isFinite(estimatedExecutionPrice) && 0 < estimatedExecutionPrice
                            ? estimatedExecutionPrice : undefined,
                    });
                } else {
                    setPreview(null);
                    setPreviewError(response?.data?.message || '无法计算订单金额');
                }
            }).catch((error: any) => {
                if (!cancelled && requestSeq === previewRequestSeqRef.current) {
                    setPreview(null);
                    const code = error?.response?.data?.code ?? error?.code;
                    const serverMessage = error?.response?.data?.message;
                    setPreviewError(stableBusinessMessage(code, serverMessage ?? '订单金额预览失败'));
                }
            }).finally(() => {
                if (!cancelled && requestSeq === previewRequestSeqRef.current) setPreviewLoading(false);
            });
        }, 300);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [open, draft, apiKeyId, spec, isFormLocked, submissionPriceValidationError, instId, aiAnalysisId, signalType,
        signalId, signalDirection, confidence, confidenceAvailable]);

    const executeOrder = useCallback(async () => {
        if (!draft || !normalizedDraft || !apiKeyId || !spec || !preview) return;
        if (submitInFlightRef.current) return;

        submitInFlightRef.current = true;
        setError(null);

        const snapPx = 'limit' === normalizedDraft.orderType
            ? normalizedDraft.entryPrice
            : undefined;

        // 冻结幂等键：首次提交时生成，重试复用
        if (!frozenKeyRef.current) {
            const keyChar = intentToKeyChar(draft.tradeIntent);
            frozenKeyRef.current = generateIdempotencyKey(aiAnalysisId, keyChar);
        }
        const idempotencyKey = frozenKeyRef.current;
        const snapshot: OrderSubmissionSnapshot = {
            analysisKey,
            apiKeyId,
            instId,
            side: 'long' === draft.posSide ? 'buy' : 'sell',
            posSide: draft.posSide,
            orderType: draft.orderType,
            allocationPercent: draft.allocationPercent,
            lever: draft.leverage,
            takeProfitPrice: normalizedDraft.targetPrice,
            stopLossPrice: normalizedDraft.stopLossPrice,
            source: 'ai',
            aiAnalysisId,
            signalType,
            signalId,
            signalDirection,
            tradeIntent: draft.tradeIntent,
            forwardConfidence: confidenceAvailable ? confidence : undefined,
            idempotencyKey,
        };
        if (undefined !== snapPx) snapshot.px = snapPx;
        const pollingDeadline = Date.now() + 5 * 60 * 1000;
        props.dispatch({type: 'FREEZE_SUBMISSION', snapshot, pollingDeadline});
        try {
            sessionStorage.setItem(pendingStorageKey, JSON.stringify({
                snapshot,
                deadline: pollingDeadline,
            }));
        } catch { /* 浏览器禁用存储时继续使用 frozenKeyRef */
        }

        try {
            const response = await tradingService.placeOrder(snapshot as any);
            const res = response as any;
            const respData = res?.data;
            if (respData?.success || res?.success) {
                const orderId = respData?.data?.orderId ?? res?.orderId;
                const manualRiskMode = true === respData?.data?.manualRiskMode
                    || true === respData?.data?.isManualRiskMode
                    || true === res?.manualRiskMode
                    || true === res?.isManualRiskMode;
                if (manualRiskMode) {
                    const text = `订单已提交审核${orderId ? ` · 审核单ID: ${orderId}` : ''}`;
                    setResult(text);
                    props.dispatch({type: 'SET_SUBMISSION_STATUS', status: 'under_review', message: text});
                    try {
                        sessionStorage.setItem(pendingStorageKey, JSON.stringify({
                            snapshot,
                            status: 'under_review',
                            message: text,
                            deadline: pollingDeadline,
                        }));
                    } catch { /* 页面内 reducer 仍会继续跟踪 */
                    }
                    message.info('订单已提交风控审核');
                } else {
                    const text = `下单成功${orderId ? ` · 订单ID: ${orderId}` : ''}`;
                    setResult(text);
                    props.dispatch({type: 'SET_SUBMISSION_STATUS', status: 'succeeded', message: text});
                    message.success('订单提交成功');
                    clearPendingSubmission();
                }
            } else if ('ORDER_PROCESSING' === respData?.code || 'ORDER_PROCESSING' === res?.code) {
                props.dispatch({type: 'SET_SUBMISSION_STATUS', status: 'processing', message: '订单正在处理中'});
                setError(null);
            } else {
                const msg = respData?.message || res?.message || '下单失败';
                props.dispatch({type: 'SET_SUBMISSION_STATUS', status: 'failed', message: msg});
                setError(msg);
                frozenKeyRef.current = null;
                clearPendingSubmission();
            }
        } catch (err: any) {
            const classified = classifySubmissionError(err);
            props.dispatch({
                type: 'SET_SUBMISSION_STATUS',
                status: classified.status,
                message: classified.message,
            });
            if ('failed' === classified.status) {
                frozenKeyRef.current = null;
                clearPendingSubmission();
                setError(classified.message);
            } else {
                setError(null);
            }
        } finally {
            submitInFlightRef.current = false;
        }
    }, [draft, normalizedDraft, apiKeyId, spec, preview, analysisKey, instId,
        aiAnalysisId, signalType, signalId, signalDirection, confidence, confidenceAvailable,
        props.dispatch, clearPendingSubmission]);

    const handleSubmit = useCallback(async () => {
        if (!draft || !normalizedDraft || !apiKeyId || !spec || !preview || submissionPriceValidationError) return;
        if ('market' === draft.orderType
            && (!preview.estimatedExecutionPrice || 0 >= preview.estimatedExecutionPrice)) return;

        // R-092：最终确认时同步复检，不能只依赖最近一次 30 秒定时检查结果。
        const latestExpiry = checkAllExpiry(expiryParams);
        if (latestExpiry.expired) {
            setError(`信号已过期：${latestExpiry.reason ?? '当前交易计划不再有效'}`);
            return;
        }

        showTradingConfirmDialog({
            direction: draft.posSide,
            instId,
            orderType: draft.orderType,
            limitPrice: 'limit' === draft.orderType ? normalizedDraft.entryPrice : undefined,
            currentPrice: preview.estimatedExecutionPrice,
            allocationPercent: draft.allocationPercent,
            lever: draft.leverage,
            estimatedMargin: preview.margin,
            estimatedQuantity: preview.size,
            notional: preview.margin * draft.leverage,
            takeProfitPrice: normalizedDraft.targetPrice,
            stopLossPrice: normalizedDraft.stopLossPrice,
            isLiveTrading: props.isLiveTrading,
            onConfirm: executeOrder,
        });
    }, [draft, normalizedDraft, apiKeyId, spec, preview, submissionPriceValidationError,
        expiryParams.currentPrice, expiryParams.entryPrice, expiryParams.expectedHoldingUntil,
        expiryParams.expectedHoldingBars, expiryParams.signalFormedAt, expiryParams.interval,
        expiryParams.zoneType, expiryParams.zoneUpper, expiryParams.zoneLower, expiryParams.latestClose,
        expiryParams.policy, instId, props.isLiveTrading, executeOrder]);

    const asDirection = signalDirection as 1 | -1 | 0;
    const sizeValidationError = preview && spec
        ? null == preview.size || !Number.isFinite(preview.size) || 0 >= preview.size
            ? '无法获得有效的预计下单数量'
            : preview.size < spec.minSz
                ? `预计数量 ${preview.size} 低于最小下单量 ${spec.minSz}` : null
        : null;
    const summaryDraft = normalizedDraft ?? draft;
    const summaryEntry = 'market' === draft?.orderType
        ? preview?.estimatedExecutionPrice : summaryDraft?.entryPrice;
    const summaryRatio = summaryDraft && summaryEntry
        ? calcRatio({...summaryDraft, entryPrice: summaryEntry}) : '--';
    const marketPriceError = 'market' === draft?.orderType && summaryDraft && summaryEntry
        ? validateTradePlanPrices({...summaryDraft, entryPrice: summaryEntry}) : null;
    const missingMarketExecutionPrice = 'market' === draft?.orderType
        && (!summaryEntry || !Number.isFinite(summaryEntry) || 0 >= summaryEntry);
    const accountError = !apiKeyId ? '请先选择有效的 API Key，再进行订单预览和提交' : null;
    const resultPending = ['processing', 'unknown', 'under_review'].includes(submitStatus);
    const submitBlockingReason = resultPending ? null
        : expiry.expired ? `信号已过期：${expiry.reason ?? '当前交易计划不再有效'}`
            : accountError ?? sizeValidationError ?? marketPriceError
            ?? (missingMarketExecutionPrice ? '无法获取有效预估市价' : null)
            ?? submissionPriceValidationError
            ?? (!spec ? '合约规格尚未加载' : null)
            ?? (!preview ? '订单预览尚未完成' : null)
            ?? (previewLoading ? '订单预览计算中' : null);
    const submitDisabled = 'submitting' === submitStatus || 'succeeded' === submitStatus
        || statusQueryLoading || null != submitBlockingReason;
    const estimatedTargetProfit = preview && summaryDraft && summaryEntry
        ? preview.margin * summaryDraft.leverage * Math.abs(summaryDraft.targetPrice - summaryEntry) / summaryEntry
        : null;
    const estimatedMaxLoss = preview && summaryDraft && summaryEntry
        ? preview.margin * summaryDraft.leverage * Math.abs(summaryEntry - summaryDraft.stopLossPrice) / summaryEntry
        : null;

    const handleClose = useCallback(() => {
        if ('idle' === submitStatus && isDirty
            && !window.confirm('当前订单草稿尚未提交，确认关闭并放弃本次编辑吗？')) return;
        onClose();
    }, [isDirty, onClose, submitStatus]);

    const handleQueryStatus = useCallback(async () => {
        const idempotencyKey = props.submissionSnapshot?.idempotencyKey ?? frozenKeyRef.current;
        if (!idempotencyKey || statusQueryLoading) return;
        setStatusQueryLoading(true);
        try {
            const response = await api.get(`/trading/signal-orders/status/${idempotencyKey}`);
            const data = response?.data?.data ?? response?.data;
            if ('SUCCEEDED' === data?.status) {
                const successMessage = formatSuccessfulOrderStatus(data);
                props.dispatch({type: 'SET_SUBMISSION_STATUS', status: 'succeeded', message: successMessage});
                setResult(successMessage);
                setError(null);
                clearPendingSubmission();
            } else if ('FAILED' === data?.status) {
                props.dispatch({
                    type: 'SET_SUBMISSION_STATUS',
                    status: 'failed',
                    message: data?.message ?? '订单处理失败'
                });
                setError(data?.message ?? '订单处理失败');
                frozenKeyRef.current = null;
                clearPendingSubmission();
            } else {
                const nextStatus = true === data?.manualRiskMode || 'under_review' === submitStatus
                    ? 'under_review' : 'processing';
                const pendingMessage = data?.message ?? ('under_review' === nextStatus ? '订单仍在审核中' : '订单仍在处理中');
                props.dispatch({type: 'SET_SUBMISSION_STATUS', status: nextStatus, message: pendingMessage});
                setError(null);
            }
        } catch {
            if ('under_review' !== submitStatus) {
                props.dispatch({type: 'SET_SUBMISSION_STATUS', status: 'unknown', message: '暂时无法查询订单结果'});
            }
            setError(null);
        } finally {
            setStatusQueryLoading(false);
        }
    }, [props.submissionSnapshot?.idempotencyKey, props.dispatch, statusQueryLoading, submitStatus, clearPendingSubmission]);

    return (
        <Drawer
            title="快捷交易"
            placement={isMobile ? 'bottom' : 'right'}
            width={isMobile ? undefined : 440}
            height={isMobile ? '90vh' : undefined}
            open={open}
            onClose={handleClose}
            closable
            maskClosable={'submitting' !== submitStatus}
            destroyOnHidden
            className="ai-quick-trading-drawer"
        >
            <div className="ai-qt-content">
                <div className="ai-qt-header">
                    <div>{instId} · {interval}</div>
                    <div style={{fontSize: 11, color: 'var(--pt-text-dim)'}}>
                        来源：信号分析 · AI 信号 · API Key #{apiKeyId ?? '--'}
                    </div>
                </div>

                {'idle' !== submitStatus && (
                    <div className={`ai-qt-status ai-qt-status-${submitStatus}`} role="status">
                        {'processing' === submitStatus ? '⏳ 订单仍在处理中，请勿重复下单'
                            : 'unknown' === submitStatus ? '⚠ 暂未确认订单结果，请勿创建新订单'
                                : 'under_review' === submitStatus ? '🛡 订单已提交审核，等待风控结果'
                                    : 'succeeded' === submitStatus ? '✅ 订单已提交'
                                        : 'failed' === submitStatus ? '❌ 订单失败'
                                            : '提交中...'}
                        {props.submissionMessage ? ` · ${props.submissionMessage}` : ''}
                    </div>
                )}
                {expiry.expired && (
                    <div className="ai-qt-error">信号已过期：{expiry.reason ?? '当前交易计划不再有效'}</div>
                )}
                {'idle' === submitStatus && (
                    <SignalDirectionSplitButton
                        aiDirection={asDirection}
                        confidence={confidenceAvailable ? confidence : Number.NaN}
                        selected={selectedKey as 'FORWARD' | 'REVERSE' | null}
                        onSelect={handleSelectDirection}
                        neutral={isNeutral}
                    />
                )}

                {selectedKey && draft && (
                    <div className="ai-qt-form">
                        <div className="ai-qt-direction-indicator">
                            {selectedKey.startsWith('REVERSE') && (
                                <div className="ai-qt-reverse-warning">
                                    ⚠ 当前选择反向观点，方向与 AI 解读相反
                                </div>
                            )}
                        </div>

                        <div className="control-row">
                            <label className="control-label">订单类型</label>
                            <div className="order-type-selector">
                                <button className={'limit' === draft.orderType ? 'active' : ''}
                                        disabled={isFormLocked}
                                        onClick={() => updateDraft({orderType: 'limit'})}>限价
                                </button>
                                <button className={'market' === draft.orderType ? 'active' : ''}
                                        disabled={isFormLocked}
                                        onClick={() => updateDraft({orderType: 'market'})}>市价
                                </button>
                            </div>
                        </div>

                        {'limit' === draft.orderType && (
                            <div className="control-row">
                                <label className="control-label">委托价</label>
                                <PriceInput value={draft.entryPrice} disabled={isFormLocked}
                                            onCommit={entryPrice => updateDraft({entryPrice})}/>
                            </div>
                        )}

                        <div className="control-row">
                            <label className="control-label">目标位</label>
                            <PriceInput value={draft.targetPrice} disabled={isFormLocked}
                                        onCommit={targetPrice => updateDraft({targetPrice})}/>
                        </div>
                        <div className="control-row">
                            <label className="control-label">止损位</label>
                            <PriceInput value={draft.stopLossPrice} disabled={isFormLocked}
                                        onCommit={stopLossPrice => updateDraft({stopLossPrice})}/>
                        </div>

                        <div className="control-row">
                            <label className="control-label">开仓比例</label>
                            <div className="amount-selector">
                                {[5, 10, 15, 25].map(pct => (
                                    <button key={pct}
                                            className={draft.allocationPercent === pct ? 'active' : ''}
                                            disabled={isFormLocked}
                                            onClick={() => updateDraft({allocationPercent: pct})}>{pct}%</button>
                                ))}
                            </div>
                        </div>

                        <div className="control-row">
                            <label className="control-label">杠杆</label>
                            <Select value={draft.leverage} onChange={value => updateDraft({leverage: value})}
                                    disabled={isFormLocked}
                                    size="small" style={{width: 120}}
                                    options={[{value: 2, label: '2x'}, {value: 3, label: '3x'}, {
                                        value: 5,
                                        label: '5x'
                                    }]}
                            />
                        </div>

                        <div className="ai-qt-summary">
                            <div className="summary-title">订单预览</div>
                            <div className="summary-row">
                                <span>方向</span>
                                <span>{'long' === draft.posSide ? '开多' : '开空'}
                                    {' · '}{draft.tradeIntent}
                                    {null != draft.forwardConfidence ? ` ${draft.forwardConfidence}%` : ''}
                                </span>
                            </div>
                            <div className="summary-row">
                                <span>入场</span>
                                <span>{'limit' === draft.orderType
                                    ? formatNormalizedPrice(draft.entryPrice, summaryDraft?.entryPrice)
                                    : summaryEntry ? `市价 · 预估 $${summaryEntry.toLocaleString()}` : '市价 · 等待预估'}</span>
                            </div>
                            <div className="summary-row">
                                <span>目标</span>
                                <span>{formatNormalizedPrice(draft.targetPrice, summaryDraft?.targetPrice)}</span>
                            </div>
                            <div className="summary-row">
                                <span>止损</span>
                                <span>{formatNormalizedPrice(draft.stopLossPrice, summaryDraft?.stopLossPrice)}</span>
                            </div>
                            <div className="summary-row">
                                <span>投入回报比</span>
                                <span>{'market' === draft.orderType ? '预估 ' : ''}1:{summaryRatio}</span>
                            </div>
                            <div className="summary-row">
                                <span>预计持仓</span>
                                <span>{props.holdingPeriodText
                                    ?? (props.expectedHoldingBars ? `${props.expectedHoldingBars} 根K线` : '--')}</span>
                            </div>
                            <div className="summary-row">
                                <span>预计保证金</span>
                                <span>{previewLoading ? '计算中...' : preview ? `$${preview.margin.toLocaleString()}` : '--'}</span>
                            </div>
                            <div className="summary-row">
                                <span>预计目标收益</span>
                                <span>{null == estimatedTargetProfit ? '--' : `≈ $${estimatedTargetProfit.toFixed(2)}`}</span>
                            </div>
                            <div className="summary-row">
                                <span>预计最大亏损</span>
                                <span>{null == estimatedMaxLoss ? '--' : `≈ $${estimatedMaxLoss.toFixed(2)}`}</span>
                            </div>
                            {preview?.size && (
                                <div className="summary-row"><span>预计数量</span><span>{preview.size}</span></div>
                            )}
                        </div>

                        {(specLoading || specError || previewError) && (
                            <div className="ai-qt-error">
                                {specLoading ? '正在加载合约规格...' : specError || previewError}
                            </div>
                        )}

                        {accountError && <div className="ai-qt-error">{accountError}</div>}

                        {submissionPriceValidationError && (
                            <div className="ai-qt-error">{submissionPriceValidationError}</div>
                        )}
                        {sizeValidationError && <div className="ai-qt-error">{sizeValidationError}</div>}
                        {marketPriceError && <div className="ai-qt-error">按预估市价校验：{marketPriceError}</div>}
                        {missingMarketExecutionPrice &&
                            <div className="ai-qt-error">无法获取有效预估市价，暂时不能提交市价单</div>}

                        {error && (
                            <div className="ai-qt-error">{error}</div>
                        )}
                        {result && (
                            <div className={'under_review' === submitStatus ? 'ai-qt-review' : 'ai-qt-success'}>
                                {result}
                                {'succeeded' === submitStatus && (
                                    <a className="ai-qt-order-link" href="/trading">查看订单</a>
                                )}
                            </div>
                        )}

                        <div className="ai-qt-actions">
                            <button className="ai-qt-cancel" disabled={'submitting' === submitStatus}
                                    onClick={handleClose}>关闭
                            </button>
                            {'failed' === submitStatus && (
                                <button className="ai-qt-cancel" onClick={() => {
                                    props.dispatch({type: 'CLEAR_SUBMISSION'});
                                    setError(null);
                                    setResult(null);
                                    frozenKeyRef.current = null;
                                }} style={{borderColor: 'var(--pt-blue)', color: 'var(--pt-blue)'}}>返回编辑</button>
                            )}
                            <button
                                className="ai-qt-submit"
                                disabled={submitDisabled}
                                title={submitBlockingReason ?? undefined}
                                onClick={resultPending ? handleQueryStatus : handleSubmit}
                            >
                                {statusQueryLoading ? '查询中...'
                                    : 'submitting' === submitStatus ? '提交中...'
                                        : 'processing' === submitStatus ? '查询结果'
                                            : 'unknown' === submitStatus ? '查询结果'
                                                : 'under_review' === submitStatus ? '查询审核结果'
                                                    : result ? '已完成'
                                                        : `确认${'long' === draft.posSide ? '开多' : '开空'}并下单`}
                            </button>
                        </div>

                        <div className="ai-qt-risk-warning">
                            ⚠ AI分析仅供参考，请控制风险
                        </div>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

function calcRatio(draft: SignalTradeDraft): string {
    const upside = Math.abs(draft.targetPrice - draft.entryPrice);
    const downside = Math.abs(draft.entryPrice - draft.stopLossPrice);
    if (0 === downside) return '0';
    return String(Math.round((upside / downside) * 10) / 10);
}

function validateMarketProtectivePrices(draft: SignalTradeDraft): string | null {
    if (!Number.isFinite(draft.targetPrice) || 0 >= draft.targetPrice
        || !Number.isFinite(draft.stopLossPrice) || 0 >= draft.stopLossPrice) {
        return '目标位和止损位必须为有效正数';
    }
    if (draft.targetPrice === draft.stopLossPrice) return '目标位和止损位不能相同';
    return null;
}

function formatNormalizedPrice(raw: number, normalized?: number): string {
    if (!Number.isFinite(normalized)) return '--';
    const formatted = `$${normalized!.toLocaleString()}`;
    return raw === normalized ? formatted : `$${raw.toLocaleString()} → ${formatted}`;
}

const PriceInput: React.FC<{
    value: number;
    disabled: boolean;
    onCommit: (value: number) => void;
}> = ({value, disabled, onCommit}) => {
    const [text, setText] = useState(String(value));
    const [invalid, setInvalid] = useState(false);
    useEffect(() => {
        setText(String(value));
        setInvalid(false);
    }, [value]);
    const commit = () => {
        const parsed = Number(text);
        if ('' !== text.trim() && Number.isFinite(parsed) && 0 < parsed) {
            setInvalid(false);
            onCommit(parsed);
        } else {
            setText(String(value));
            setInvalid(true);
        }
    };
    return <div className="ai-qt-price-field">
        <input type="text" inputMode="decimal" className={`ai-qt-input${invalid ? ' invalid' : ''}`}
               value={text} disabled={disabled}
               onChange={event => {
                   setText(event.target.value);
                   setInvalid(false);
               }}
               onBlur={commit}
               onKeyDown={event => {
                   if ('Enter' === event.key) commit();
               }}/>
        {invalid && <div className="ai-qt-input-error">请输入有效正数</div>}
    </div>;
};

function getSessionDraft(
    session: AiTradeSession | NeutralTradeSession,
    key: DraftKey | null,
): SignalTradeDraft | null {
    if (null == key) return null;
    if ('FORWARD' === key && 'forwardDraft' in session) return session.forwardDraft;
    if ('REVERSE' === key && 'reverseDraft' in session) return session.reverseDraft;
    if ('MANUAL_LONG' === key && 'manualLongDraft' in session) return session.manualLongDraft;
    if ('MANUAL_SHORT' === key && 'manualShortDraft' in session) return session.manualShortDraft;
    return null;
}
